// Human-editable add-person pane. A directory administrator fills ordinary fields and saves one
// atomic person record; someone without that authority keeps the suggestion lane. Access is clearly
// separate from the paragraph describing what the person actually does in the company.

import { useState } from 'react';
import { Btn } from '../primitives.tsx';
import type { RouteResult } from '../api-client.ts';
import type { DirectoryRole } from '../know-projections.ts';
import { ACCESS_LEVEL_LABEL, ACCESS_LEVEL_ORDER } from './team-directory.ts';

export interface NewDirectoryPerson {
  readonly displayName: string;
  readonly responsibilities: string;
  readonly emailAddresses: readonly string[];
  readonly accessLevel: DirectoryRole;
}

function formIsComplete(
  displayName: string,
  responsibilities: string,
  emailAddresses: readonly string[],
): boolean {
  return (
    displayName.trim().length > 0 &&
    responsibilities.trim().length > 0 &&
    emailAddresses.length > 0 &&
    emailAddresses.every((address) => address.trim().length > 0)
  );
}

function newDirectoryPerson(
  displayName: string,
  responsibilities: string,
  emailAddresses: readonly string[],
  accessLevel: DirectoryRole,
): NewDirectoryPerson {
  return {
    displayName: displayName.trim(),
    responsibilities: responsibilities.trim(),
    emailAddresses: emailAddresses.map((address) => address.trim()),
    accessLevel,
  };
}

function PersonDescriptionFields({
  displayName,
  responsibilities,
  onName,
  onResponsibilities,
}: {
  readonly displayName: string;
  readonly responsibilities: string;
  readonly onName: (value: string) => void;
  readonly onResponsibilities: (value: string) => void;
}): React.JSX.Element {
  return (
    <>
      <label className="tm-form-field">
        <span className="mono-label">Name</span>
        <input
          type="text"
          value={displayName}
          aria-label="Name"
          placeholder="Jo Bloggs"
          onChange={(event) => onName(event.target.value)}
        />
      </label>
      <label className="tm-form-field">
        <span className="mono-label">What they do</span>
        <textarea
          rows={4}
          value={responsibilities}
          aria-label="What they do"
          placeholder="e.g. Handles production, quality control and scheduling."
          onChange={(event) => onResponsibilities(event.target.value)}
        />
      </label>
    </>
  );
}

function EmailAddressRow({
  address,
  index,
  removable,
  onChange,
  onRemove,
}: {
  readonly address: string;
  readonly index: number;
  readonly removable: boolean;
  readonly onChange: (value: string) => void;
  readonly onRemove: () => void;
}): React.JSX.Element {
  return (
    <div className="tm-email-field">
      <input
        type="email"
        value={address}
        aria-label={`Email address ${index + 1}`}
        placeholder="name@company.co.uk"
        onChange={(event) => onChange(event.target.value)}
      />
      {removable ? (
        <Btn kind="ghost" sm ariaLabel={`Remove email address ${index + 1}`} onClick={onRemove}>
          Remove
        </Btn>
      ) : null}
    </div>
  );
}

function EmailAddressFields({
  addresses,
  onChange,
}: {
  readonly addresses: readonly string[];
  readonly onChange: (addresses: readonly string[]) => void;
}): React.JSX.Element {
  return (
    <div className="tm-form-field">
      <span className="mono-label">Email addresses</span>
      {addresses.map((address, index) => (
        <EmailAddressRow
          key={index}
          address={address}
          index={index}
          removable={addresses.length > 1}
          onChange={(value) =>
            onChange(addresses.map((item, itemIndex) => (itemIndex === index ? value : item)))
          }
          onRemove={() => onChange(addresses.filter((_item, itemIndex) => itemIndex !== index))}
        />
      ))}
      <Btn
        kind="ghost"
        sm
        ariaLabel="Add another email address"
        onClick={() => onChange([...addresses, ''])}
      >
        Add another email
      </Btn>
    </div>
  );
}

function AccessLevelField({
  value,
  onChange,
}: {
  readonly value: DirectoryRole;
  readonly onChange: (value: DirectoryRole) => void;
}): React.JSX.Element {
  return (
    <label className="tm-form-field">
      <span className="mono-label">Access level</span>
      <select
        value={value}
        aria-label="Access level"
        onChange={(event) => onChange(event.target.value as DirectoryRole)}
      >
        {ACCESS_LEVEL_ORDER.map((level) => (
          <option key={level} value={level}>
            {ACCESS_LEVEL_LABEL[level]}
          </option>
        ))}
      </select>
      <span className="tm-sec-hint">Permissions only — this is not their role in the company.</span>
    </label>
  );
}

/** The old suggestion form remains only for a viewer who cannot save directory changes. */
function SuggestPerson({
  onSubmit,
}: {
  readonly onSubmit: (summary: string) => void;
}): React.JSX.Element {
  const [summary, setSummary] = useState('');
  const empty = summary.trim().length === 0;
  return (
    <>
      <p className="tm-add-sub">
        Describe who should be added. Someone with team-directory access will make the change.
      </p>
      <textarea
        rows={4}
        value={summary}
        aria-label="Describe the person to add"
        placeholder="e.g. Jo handles invoice chasing and supplier payments — jo@company.co.uk"
        onChange={(event) => setSummary(event.target.value)}
      />
      <Btn
        kind="primary"
        sm
        disabled={empty}
        ariaLabel="Send the add suggestion"
        onClick={() => {
          if (!empty) onSubmit(summary.trim());
        }}
      >
        Send suggestion
      </Btn>
    </>
  );
}

/** Direct form: name, an agent-readable description, one-or-more addresses, then access. */
function CreatePerson({
  onSave,
  onSaved,
}: {
  readonly onSave: (person: NewDirectoryPerson) => Promise<RouteResult>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [emailAddresses, setEmailAddresses] = useState<readonly string[]>(['']);
  const [accessLevel, setAccessLevel] = useState<DirectoryRole>('member');
  const [saving, setSaving] = useState(false);
  const complete = formIsComplete(displayName, responsibilities, emailAddresses);

  const save = async (): Promise<void> => {
    if (!complete || saving) return;
    setSaving(true);
    try {
      const result = await onSave(
        newDirectoryPerson(displayName, responsibilities, emailAddresses, accessLevel),
      );
      if (result.ok) onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <p className="tm-add-sub">
        Add them once. Ritona reads “What they do” when helping the team.
      </p>
      <PersonDescriptionFields
        displayName={displayName}
        responsibilities={responsibilities}
        onName={setDisplayName}
        onResponsibilities={setResponsibilities}
      />
      <EmailAddressFields addresses={emailAddresses} onChange={setEmailAddresses} />
      <AccessLevelField value={accessLevel} onChange={setAccessLevel} />
      <Btn
        kind="primary"
        sm
        disabled={!complete || saving}
        ariaLabel="Save the person"
        onClick={() => void save()}
      >
        {saving ? 'Saving…' : 'Save person'}
      </Btn>
    </>
  );
}

export function TeamAddPane({
  canCreateDirectly,
  onCreate,
  onSuggest,
  onCancel,
}: {
  readonly canCreateDirectly: boolean;
  readonly onCreate: (person: NewDirectoryPerson) => Promise<RouteResult>;
  readonly onSuggest: (summary: string) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  return (
    <section aria-label="Add someone" className="tm-detail">
      <div className="tm-card tm-add-card" data-add-person-form>
        <h2 className="tm-add-title">Add someone</h2>
        {canCreateDirectly ? (
          <CreatePerson onSave={onCreate} onSaved={onCancel} />
        ) : (
          <SuggestPerson onSubmit={onSuggest} />
        )}
        <Btn kind="ghost" sm ariaLabel="Cancel adding someone" onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </section>
  );
}
